<script setup lang="ts">
    import { computed, onMounted, ref } from 'vue';
    import type { CheckoutState, GdprCheckoutPagePayload } from './types';
    import { clearCheckout, currencySymbol, readCheckout, writeCheckout } from './checkout';
    import PrototypeIcon from './PrototypeIcon.vue';
    import PrototypePlanCard from './PrototypePlanCard.vue';

    const props = defineProps<{
        payload: GdprCheckoutPagePayload;
    }>();

    const checkoutState = ref<CheckoutState>(readCheckout());
    const formEmail = ref(checkoutState.value.email ?? props.payload.demoAccount.email);
    const formFirstName = ref(checkoutState.value.firstName ?? props.payload.demoAccount.firstName);
    const formLastName = ref(checkoutState.value.lastName ?? props.payload.demoAccount.lastName);
    const formCompany = ref(checkoutState.value.company ?? props.payload.demoAccount.company);
    const formPassword = ref(props.payload.demoAccount.password);
    const formConfirmPassword = ref(props.payload.demoAccount.password);
    const formError = ref<string | null>(null);

    const currency = computed(() => currencySymbol(props.payload.plan.currency));
    const fullName = computed(() => [checkoutState.value.firstName, checkoutState.value.lastName].filter(Boolean).join(' ') || '—');

    const routes = {
        plan: props.payload.homeUrl,
        email: '/subscribe/email',
        verify: '/subscribe/verify',
        register: '/subscribe/register',
        login: '/subscribe/login',
        summary: '/subscribe/summary',
        payment: '/subscribe/payment',
        success: '/subscribe/success',
    };

    function navigate(url: string): void {
        window.location.assign(url);
    }

    function refreshState(): void {
        checkoutState.value = readCheckout();
    }

    onMounted(() => {
        refreshState();

        if (props.payload.step !== 'plan' && !checkoutState.value.planId) {
            navigate(routes.plan);
            return;
        }

        if (['verify', 'register', 'summary', 'payment'].includes(props.payload.step) && !checkoutState.value.email) {
            navigate(routes.email);
        }
    });

    function selectPlan(): void {
        writeCheckout({ planId: props.payload.plan.id });
        navigate(routes.email);
    }

    function submitEmail(event: Event): void {
        event.preventDefault();
        formError.value = null;

        const value = formEmail.value.trim().toLowerCase();

        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
            formError.value = props.payload.content.emailValidationMessage ?? 'Voer een geldig e-mailadres in.';
            return;
        }

        writeCheckout({ planId: checkoutState.value.planId ?? props.payload.plan.id, email: value });
        navigate(routes.verify);
    }

    function simulateVerify(): void {
        navigate(routes.register);
    }

    function submitRegister(event: Event): void {
        event.preventDefault();
        formError.value = null;

        if (!formFirstName.value.trim() || !formLastName.value.trim()) {
            formError.value = props.payload.content.nameValidationMessage ?? 'Vul je voor- en achternaam in.';
            return;
        }

        if (formPassword.value !== formConfirmPassword.value) {
            formError.value = props.payload.content.passwordMismatchMessage ?? 'De wachtwoorden komen niet overeen.';
            return;
        }

        writeCheckout({
            planId: checkoutState.value.planId ?? props.payload.plan.id,
            email: formEmail.value.trim().toLowerCase(),
            firstName: formFirstName.value.trim(),
            lastName: formLastName.value.trim(),
            company: formCompany.value.trim() || undefined,
        });

        navigate(routes.summary);
    }

    function submitLogin(event: Event): void {
        event.preventDefault();
        writeCheckout({
            planId: checkoutState.value.planId ?? props.payload.plan.id,
            email: formEmail.value.trim().toLowerCase(),
            firstName: props.payload.demoAccount.firstName,
            lastName: props.payload.demoAccount.lastName,
            company: props.payload.demoAccount.company,
        });
        navigate(routes.summary);
    }

    function goToPayment(): void {
        navigate(routes.payment);
    }

    function continueToSuccess(): void {
        navigate(routes.success);
    }

    function finishFlow(): void {
        clearCheckout();
        navigate(props.payload.workspaceUrl);
    }
</script>

<template>
    <div class="gdpr-prototype gdpr-checkout">
        <header class="gdpr-topbar gdpr-topbar--checkout">
            <div class="gdpr-shell gdpr-topbar__inner">
                <a class="gdpr-brand" :href="payload.workspaceUrl">
                    <div class="gdpr-brand__mark">
                        <PrototypeIcon name="shield" />
                    </div>
                    <span>{{ payload.siteName }}</span>
                </a>

                <div class="gdpr-checkout__secure">
                    <PrototypeIcon name="lock" />
                    <span>{{ payload.secureCheckoutLabel }}</span>
                </div>
            </div>
        </header>

        <div class="gdpr-checkout__notice">
            <div class="gdpr-shell">
                <p>{{ payload.prototypeNotice }}</p>
            </div>
        </div>

        <main class="gdpr-shell gdpr-checkout__main">
            <template v-if="payload.step === 'plan'">
                <section class="gdpr-form-card" data-cy="checkout-plan">
                    <div class="gdpr-form-card__intro">
                        <h1>{{ payload.content.heading }}</h1>
                        <p>{{ payload.content.introText }}</p>
                    </div>

                    <PrototypePlanCard :plan="payload.plan">
                        <template #action>
                            <button class="gdpr-button gdpr-button--primary gdpr-button--full" data-cy="select-plan" type="button" @click="selectPlan">
                                <span>{{ payload.content.primaryButtonLabel }}</span>
                                <PrototypeIcon name="arrowRight" />
                            </button>
                        </template>
                    </PrototypePlanCard>
                </section>
            </template>

            <template v-else-if="payload.step === 'email'">
                <section class="gdpr-form-card" data-cy="checkout-email">
                    <div class="gdpr-form-card__intro">
                        <h1>{{ payload.content.heading }}</h1>
                        <p>{{ payload.content.introText }}</p>
                    </div>

                    <form class="gdpr-form" @submit="submitEmail">
                        <label class="gdpr-form__field">
                            <span>{{ payload.content.emailLabel }}</span>
                            <input v-model="formEmail" data-cy="email-input" type="email" :placeholder="payload.content.emailPlaceholder ?? ''" />
                        </label>

                        <p v-if="formError" class="gdpr-form__error">
                            <PrototypeIcon name="alert" />
                            <span>{{ formError }}</span>
                        </p>

                        <button class="gdpr-button gdpr-button--primary gdpr-button--full" data-cy="email-submit" type="submit">
                            <span>{{ payload.content.primaryButtonLabel }}</span>
                            <PrototypeIcon name="arrowRight" />
                        </button>

                        <p class="gdpr-form__footer">
                            {{ payload.content.secondaryText }}
                            <a :href="routes.login">{{ payload.content.secondaryLinkLabel }}</a>
                        </p>
                    </form>
                </section>
            </template>

            <template v-else-if="payload.step === 'verify'">
                <section class="gdpr-form-card gdpr-form-card--centered" data-cy="checkout-verify">
                    <div class="gdpr-circle-icon">
                        <PrototypeIcon name="mail" />
                    </div>
                    <h1>{{ payload.content.heading }}</h1>
                    <p>
                        {{ payload.content.introText }}
                        <strong>{{ checkoutState.email ?? payload.demoAccount.email }}</strong>.
                    </p>
                    <div class="gdpr-inline-note">
                        <PrototypeIcon name="checkCircle" />
                        <span>{{ payload.content.noteText }}</span>
                    </div>
                    <button class="gdpr-button gdpr-button--primary gdpr-button--full" data-cy="verify-submit" type="button" @click="simulateVerify">
                        <span>{{ payload.content.primaryButtonLabel }}</span>
                        <PrototypeIcon name="arrowRight" />
                    </button>
                </section>
            </template>

            <template v-else-if="payload.step === 'register'">
                <section class="gdpr-form-card" data-cy="checkout-register">
                    <div class="gdpr-form-card__intro">
                        <h1>{{ payload.content.heading }}</h1>
                        <p>{{ payload.content.introText }}</p>
                    </div>

                    <form class="gdpr-form" @submit="submitRegister">
                        <div class="gdpr-form__grid">
                            <label class="gdpr-form__field">
                                <span>{{ payload.content.firstNameLabel }}</span>
                                <input v-model="formFirstName" data-cy="register-first-name" type="text" autocomplete="given-name" />
                            </label>
                            <label class="gdpr-form__field">
                                <span>{{ payload.content.lastNameLabel }}</span>
                                <input v-model="formLastName" data-cy="register-last-name" type="text" autocomplete="family-name" />
                            </label>
                        </div>

                        <label class="gdpr-form__field">
                            <span>{{ payload.content.companyLabel }}</span>
                            <input v-model="formCompany" data-cy="register-company" type="text" autocomplete="organization" />
                        </label>

                        <label class="gdpr-form__field">
                            <span>{{ payload.content.emailLabel }}</span>
                            <input v-model="formEmail" data-cy="register-email" type="email" autocomplete="email" />
                        </label>

                        <div class="gdpr-form__grid">
                            <label class="gdpr-form__field">
                                <span>{{ payload.content.passwordLabel }}</span>
                                <input v-model="formPassword" data-cy="register-password" type="password" autocomplete="new-password" />
                            </label>
                            <label class="gdpr-form__field">
                                <span>{{ payload.content.confirmPasswordLabel }}</span>
                                <input v-model="formConfirmPassword" data-cy="register-confirm" type="password" autocomplete="new-password" />
                            </label>
                        </div>

                        <p v-if="formError" class="gdpr-form__error">
                            <PrototypeIcon name="alert" />
                            <span>{{ formError }}</span>
                        </p>

                        <button class="gdpr-button gdpr-button--primary gdpr-button--full" data-cy="register-submit" type="submit">
                            <span>{{ payload.content.primaryButtonLabel }}</span>
                            <PrototypeIcon name="arrowRight" />
                        </button>

                        <p class="gdpr-form__footer">
                            {{ payload.content.secondaryText }}
                            <a :href="routes.login">{{ payload.content.secondaryLinkLabel }}</a>
                        </p>
                    </form>
                </section>
            </template>

            <template v-else-if="payload.step === 'login'">
                <section class="gdpr-form-card" data-cy="checkout-login">
                    <div class="gdpr-form-card__intro">
                        <h1>{{ payload.content.heading }}</h1>
                        <p>{{ payload.content.introText }}</p>
                    </div>

                    <form class="gdpr-form" @submit="submitLogin">
                        <label class="gdpr-form__field">
                            <span>{{ payload.content.emailLabel }}</span>
                            <input v-model="formEmail" data-cy="login-email" type="email" autocomplete="email" />
                        </label>
                        <label class="gdpr-form__field">
                            <span>{{ payload.content.passwordLabel }}</span>
                            <input v-model="formPassword" data-cy="login-password" type="password" autocomplete="current-password" />
                        </label>
                        <button class="gdpr-button gdpr-button--primary gdpr-button--full" data-cy="login-submit" type="submit">
                            <span>{{ payload.content.primaryButtonLabel }}</span>
                            <PrototypeIcon name="arrowRight" />
                        </button>
                        <p class="gdpr-form__footer">
                            {{ payload.content.secondaryText }}
                            <a :href="routes.register">{{ payload.content.secondaryLinkLabel }}</a>
                        </p>
                    </form>
                </section>
            </template>

            <template v-else-if="payload.step === 'summary'">
                <section class="gdpr-form-card" data-cy="checkout-summary">
                    <div class="gdpr-form-card__intro">
                        <h1>{{ payload.content.heading }}</h1>
                        <p>{{ payload.content.introText }}</p>
                    </div>

                    <div class="gdpr-stack">
                        <PrototypePlanCard :plan="payload.plan" compact />

                        <section class="gdpr-summary-card">
                            <header>
                                <h2>{{ payload.content.customerSectionTitle }}</h2>
                                <a :href="routes.register">
                                    <PrototypeIcon name="pencil" />
                                    <span>{{ payload.content.editLinkLabel }}</span>
                                </a>
                            </header>
                            <dl>
                                <div><dt>{{ payload.content.customerNameLabel }}</dt><dd>{{ fullName }}</dd></div>
                                <div><dt>{{ payload.content.customerEmailLabel }}</dt><dd>{{ checkoutState.email ?? '—' }}</dd></div>
                                <div v-if="checkoutState.company"><dt>{{ payload.content.customerCompanyLabel }}</dt><dd>{{ checkoutState.company }}</dd></div>
                            </dl>
                        </section>

                        <section class="gdpr-summary-card">
                            <header>
                                <h2>{{ payload.content.billingSectionTitle }}</h2>
                            </header>
                            <dl>
                                <div><dt>{{ payload.content.billingAddressLabel }}</dt><dd class="is-muted">{{ payload.content.billingPlaceholderText }}</dd></div>
                            </dl>
                        </section>

                        <section class="gdpr-summary-card">
                            <dl class="gdpr-pricing-lines">
                                <div><dt>{{ payload.plan.name }} · {{ payload.content.monthlyLineLabel }}</dt><dd>{{ currency }}{{ payload.plan.monthlyPrice }},00</dd></div>
                                <div><dt>{{ payload.content.vatLineLabel }}</dt><dd class="is-muted">{{ payload.content.vatCalculationText }}</dd></div>
                                <div class="is-total"><dt>{{ payload.content.totalLineLabel }}</dt><dd>{{ currency }}{{ payload.plan.monthlyPrice }},00</dd></div>
                            </dl>
                            <button class="gdpr-button gdpr-button--primary gdpr-button--full" data-cy="summary-submit" type="button" @click="goToPayment">
                                <span>{{ payload.content.primaryButtonLabel }}</span>
                                <PrototypeIcon name="arrowRight" />
                            </button>
                            <p class="gdpr-summary-card__hint">{{ payload.content.paymentHint }}</p>
                        </section>
                    </div>
                </section>
            </template>

            <template v-else-if="payload.step === 'payment'">
                <section class="gdpr-form-card gdpr-form-card--centered" data-cy="checkout-payment">
                    <div class="gdpr-circle-icon">
                        <PrototypeIcon name="lock" />
                    </div>
                    <h1>{{ payload.content.heading }}</h1>
                    <p>{{ payload.content.introText }}</p>
                    <div class="gdpr-placeholder-box">{{ payload.content.placeholderText }}</div>
                    <button class="gdpr-button gdpr-button--primary gdpr-button--full" data-cy="payment-submit" type="button" @click="continueToSuccess">
                        <span>{{ payload.content.primaryButtonLabel }}</span>
                        <PrototypeIcon name="arrowRight" />
                    </button>
                </section>
            </template>

            <template v-else-if="payload.step === 'success'">
                <section class="gdpr-form-card gdpr-form-card--centered" data-cy="checkout-success">
                    <div class="gdpr-circle-icon">
                        <PrototypeIcon name="checkCircle" />
                    </div>
                    <h1>{{ payload.content.heading }}</h1>
                    <p>
                        {{ payload.content.introText }}
                        <strong>{{ checkoutState.email ?? payload.demoAccount.email }}</strong>.
                    </p>
                    <dl class="gdpr-success-summary">
                        <div><dt>{{ payload.content.subscriptionLineLabel }}</dt><dd>{{ payload.plan.name }}</dd></div>
                        <div><dt>{{ payload.content.amountLineLabel }}</dt><dd>{{ currency }}{{ payload.plan.monthlyPrice }},00 {{ payload.content.amountSuffixLabel }}</dd></div>
                        <div><dt>{{ payload.content.orderIdLineLabel }}</dt><dd>{{ payload.demoAccount.orderId }}</dd></div>
                    </dl>
                    <button class="gdpr-button gdpr-button--primary gdpr-button--full" data-cy="success-submit" type="button" @click="finishFlow">
                        <span>{{ payload.content.primaryButtonLabel }}</span>
                        <PrototypeIcon name="arrowRight" />
                    </button>
                </section>
            </template>
        </main>
    </div>
</template>
